import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import KycModal from "components/KycModal";
import { useFormattedMessage } from "hooks";
import { ACCOUNT_HASH_VARIABLES, TENANTS_LIST } from "constants/index";
import { KYC_STATUS_LOCAL } from "constants/status";
import ArrowNext from "assets/images/svg/next.svg";
import { addOverlay } from "utils";
import { selectKYCServerError } from "store/user/kyc/selectors";
import { selectTenant } from "store/app/selectors";
import { selectIsSparkasseAccountConnected } from "store/user/account/selectors";

const messages = {
    kycTileTitle: "your_identification",
    kycTileContent: "trust_and_security",
    kycTileErrorContent: "error_identifying",
    kycTileSuccessContent: "identification_completed",
    kycTileInitiatedContent: "identification_started",
    kycTilePendingContent: "confirmation_by_email",
    kycTileShowReKYCPendingContent: "identification_data_outdated",
    kycTileInitiatedReKYCPendingContent: "change_registration_address",
    kycServerError: "kyc_server_error",
};

const WrappedKycModal = ({
    currentKYCStatus,
    setKycModalFlag,
    kycModalFlag,
    addHash,
    removeHash,
    isKYCServerError,
    currentTenant,
    isSparkasseAccountConnected,
}) => {
    const { formatMessage } = useFormattedMessage();

    // Memoize message mapping
    const kycMessages = useMemo(
        () => ({
            [KYC_STATUS_LOCAL.ERROR]: formatMessage(messages.kycTileErrorContent),
            [KYC_STATUS_LOCAL.COMPLETED]: formatMessage(messages.kycTileSuccessContent),
            [KYC_STATUS_LOCAL.INITIATED]: formatMessage(messages.kycTileInitiatedContent),
            [KYC_STATUS_LOCAL.PENDING]: formatMessage(messages.kycTilePendingContent),
            [KYC_STATUS_LOCAL.REKYC]: formatMessage(messages.kycTileShowReKYCPendingContent),
            [KYC_STATUS_LOCAL.REKYC_PENDING]: formatMessage(messages.kycTileInitiatedReKYCPendingContent),
        }),
        [formatMessage],
    );

    const statusClassName = useMemo(
        () => ({
            [KYC_STATUS_LOCAL.COMPLETED]: "tile-success",
            [KYC_STATUS_LOCAL.INITIATED]: "tile-initiate",
            [KYC_STATUS_LOCAL.PENDING]: "tile-deactive",
            [KYC_STATUS_LOCAL.ERROR]: "tile-error",
            // legacy mappings (for backward compatibility)
            success: "tile-success",
            deactivate: "tile-deactive",
            initiated: "tile-initiate",
            pending: "tile-deactive",
            error: "tile-error",
        }),
        [],
    );

    useEffect(() => {
        if (kycModalFlag) addOverlay();
    }, [kycModalFlag]);

    const handleCloseModal = () => {
        const fromSparkasseIdentification = sessionStorage.getItem("fromSparkasseIdentification");
        if (fromSparkasseIdentification === "true") {
            sessionStorage.removeItem("fromSparkasseIdentification");
            window.location.hash = ACCOUNT_HASH_VARIABLES.ACCOUNT_LINK_SPARKASSE_IDENTIFICATION;
        } else {
            removeHash();
        }
        setKycModalFlag(false);
    };

    const getKycMessage = (status) => kycMessages[status] || formatMessage(messages.kycTileContent);

    return (
        <>
            <KycModal open={kycModalFlag} onBackButtonClick={handleCloseModal} onCloseButtonClick={handleCloseModal} />

            <div
                className={`identity-block identification-block border-radius pointer bg-primary-light ${
                    statusClassName[currentKYCStatus] || ""
                }`}
                onClick={() => {
                    if (!isKYCServerError) {
                        if (currentTenant === TENANTS_LIST.SACHWERTINVEST && !isSparkasseAccountConnected) {
                            addHash?.(ACCOUNT_HASH_VARIABLES.ACCOUNT_LINK_SPARKASSE_IDENTIFICATION);
                        } else {
                            setKycModalFlag(true);
                            addHash?.(ACCOUNT_HASH_VARIABLES.ACCOUNT_KYC);
                        }
                    }
                }}
            >
                <div className="d-flex justify-between slign-center">
                    <span className="body-medium">{formatMessage(messages.kycTileTitle)}</span>
                    {!isKYCServerError && (
                        <span className="next-arrow">
                            <ArrowNext />
                        </span>
                    )}
                </div>

                <div className="description">
                    <div className="my-0 text-16 fw-600">
                        {isKYCServerError ? formatMessage(messages.kycServerError) : getKycMessage(currentKYCStatus)}
                    </div>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    isKYCServerError: selectKYCServerError,
    currentTenant: selectTenant,
    isSparkasseAccountConnected: selectIsSparkasseAccountConnected,
});

export default connect(mapStateToProps)(WrappedKycModal);
